import { ApiError, unwrap, type components } from "@sagot/api-client";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";

type Schemas = components["schemas"];
type Sections = NonNullable<Schemas["PackPlanOut"]["sections"]>;

export type RuleItem = Sections["routing"]["mandatory_handover"][number];
export type TierItem = Sections["routing"]["tiers"][number];

/** The required-handover rules and tier priorities of the pack the workspace applied (none: `key` is null). */
export interface AppliedRules {
  key: string | null;
  rules: readonly RuleItem[];
  tiers: readonly TierItem[];
}

const STALE_MS = 30_000;
const NONE: AppliedRules = { key: null, rules: [], tiers: [] };
const NO_PACK_STATUSES: ReadonlySet<number> = new Set([402, 403, 404]);

/** `GET /v1/service-packs` for the applied key, then `POST /v1/service-packs/{key}/preview` (writes nothing) for its routing sections. */
export function useAppliedRules(): UseQueryResult<AppliedRules> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: ["ws", ws, "agent-settings", "applied-rules"],
    queryFn: async (): Promise<AppliedRules> => {
      // A workspace without the Service Desk (404, 402) or a member who cannot configure it (403) has no pack to show.
      const list = await unwrap(api.GET("/v1/service-packs")).catch((error: unknown) => {
        if (error instanceof ApiError && NO_PACK_STATUSES.has(error.status)) return null;
        throw error;
      });
      const key = list?.applied_key ?? null;
      if (key === null) return NONE;
      const plan = await unwrap(api.POST("/v1/service-packs/{key}/preview", { params: { path: { key } } })).catch((error: unknown) => {
        if (error instanceof ApiError && NO_PACK_STATUSES.has(error.status)) return null;
        throw error;
      });
      if (plan === null) return NONE;
      const routing = plan.sections?.routing;
      return { key, rules: routing?.mandatory_handover ?? [], tiers: routing?.tiers ?? [] };
    },
    retry: false,
    staleTime: STALE_MS,
  });
}
